import { useCallback, useId, useMemo, useRef } from "react";
import {
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArchive,
  IconBrandDocker,
  IconBrandReact,
  IconDownload,
  IconCloud,
  IconCloudComputing,
  IconCloudDataConnection,
  IconDeviceDesktop,
  IconGraph,
  IconRobot,
  IconStack2,
} from "@tabler/icons-react";
import { DiagramNodePill, exportSvgToPng } from "./diagramHelpers";
import { DOC_LINKS_STACK, TIERS } from "../data/architectureStack";

interface TierLayout {
  title: string;
  color: string;
  y: number;
  height: number;
  nodes: {
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }[];
}

const VIEWBOX_WIDTH = 1400;
const VIEWBOX_HEIGHT = 900;
const TIER_HEIGHT = 160;
const TIER_GAP = 42;
const TIER_PADDING_X = 88;
const NODE_GAP = 24;
const NODE_HEIGHT = 56;
const BACKGROUND_COLOR = "#0e0f12";
const CARD_BORDER = "rgba(122, 169, 255, 0.2)";
const TEXT_PRIMARY = "#f1f5f9";
const TEXT_MUTED = "rgba(241, 245, 249, 0.7)";

const NODE_ICONS: Record<string, JSX.Element> = {
  "Client Browser": <IconDeviceDesktop stroke={2} />,
  "Cloud CDN & HTTPS LB": <IconCloud stroke={2} />,
  "NGINX Ingress": <IconCloud stroke={2} />,
  "React Frontend (Vite + Mantine)": <IconBrandReact stroke={2} />,
  "GraphQL API (Fastify/Apollo)": <IconGraph stroke={2} />,
  "Prisma ORM": <IconStack2 stroke={2} />,
  "PostgreSQL (Cloud SQL)": <IconCloudDataConnection stroke={2} />,
  "Redis Cache": <IconArchive stroke={2} />,
  Docker: <IconBrandDocker stroke={2} />,
  "Artifact Registry": <IconArchive stroke={2} />,
  "GKE Staging": <IconCloudComputing stroke={2} />,
  "GKE Production": <IconCloudComputing stroke={2} />,
  "GitHub Actions CI": <IconRobot stroke={2} />,
};

function hexToRgba(hex: string, alpha: number): string {
  const sanitized = hex.replace("#", "");
  const bigint = Number.parseInt(sanitized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function ArchitectureStackDiagram() {
  const svgRef = useRef<globalThis.SVGSVGElement | null>(null);
  const arrowMarkerId = useId();
  const shadowId = useId();

  const tiers = useMemo<TierLayout[]>(() => {
    const totalHeight =
      TIERS.length * TIER_HEIGHT + (TIERS.length - 1) * TIER_GAP;
    const topOffset = (VIEWBOX_HEIGHT - totalHeight) / 2;

    return TIERS.map((tier, index) => {
      const y = topOffset + index * (TIER_HEIGHT + TIER_GAP);
      const availableWidth = VIEWBOX_WIDTH - TIER_PADDING_X * 2;
      const nodeCount = tier.nodes.length;
      const nodeWidth = Math.min(
        260,
        (availableWidth - NODE_GAP * (nodeCount - 1)) / nodeCount
      );
      const startX =
        TIER_PADDING_X +
        (availableWidth -
          (nodeWidth * nodeCount + NODE_GAP * (nodeCount - 1))) /
          2;

      const nodes = tier.nodes.map((label, nodeIndex) => {
        const x = startX + nodeIndex * (nodeWidth + NODE_GAP);
        const yOffset = y + TIER_HEIGHT / 2 - NODE_HEIGHT / 2;
        return {
          label,
          x,
          y: yOffset,
          width: nodeWidth,
          height: NODE_HEIGHT,
        };
      });

      return {
        title: tier.title,
        color: tier.color,
        y,
        height: TIER_HEIGHT,
        nodes,
      };
    });
  }, []);

  const downloadPng = useCallback(() => {
    if (svgRef.current) {
      exportSvgToPng(
        svgRef.current,
        "architecture-stack.png",
        BACKGROUND_COLOR
      );
    }
  }, []);

  return (
    <Card
      withBorder
      radius="lg"
      padding="lg"
      shadow="sm"
      style={{ backgroundColor: BACKGROUND_COLOR, borderColor: CARD_BORDER }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Title order={3} c={TEXT_PRIMARY}>
              Architecture Stack Overview
            </Title>
            <Text size="sm" c={TEXT_MUTED}>
              Tiered deployment view of the Dachshund platform
            </Text>
          </Stack>
          <Tooltip label="Download a PNG snapshot" withinPortal>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={downloadPng}
            >
              Export PNG
            </Button>
          </Tooltip>
        </Group>

        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="Architecture stack diagram"
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <defs>
            <marker
              id={arrowMarkerId}
              markerWidth="14"
              markerHeight="14"
              refX="7"
              refY="7"
              orient="auto"
            >
              <path d="M0,0 L0,14 L12,7 z" fill="rgba(74, 215, 209, 0.9)" />
            </marker>
            <filter id={shadowId} x="-10%" y="-20%" width="130%" height="160%">
              <feDropShadow
                dx="0"
                dy="6"
                stdDeviation="8"
                floodColor="#000"
                floodOpacity="0.35"
              />
            </filter>
          </defs>

          <rect
            width={VIEWBOX_WIDTH}
            height={VIEWBOX_HEIGHT}
            rx={36}
            fill={BACKGROUND_COLOR}
          />

          {tiers.map((tier, index) => {
            const accentFill = hexToRgba(tier.color, 0.16);
            const accentStroke = hexToRgba(tier.color, 0.45);
            return (
              <g key={tier.title}>
                <rect
                  x={TIER_PADDING_X - 40}
                  y={tier.y}
                  width={VIEWBOX_WIDTH - (TIER_PADDING_X - 40) * 2}
                  height={tier.height}
                  rx={28}
                  fill={accentFill}
                  stroke={accentStroke}
                  strokeWidth={1.6}
                />
                <text
                  x={TIER_PADDING_X}
                  y={tier.y + 42}
                  fill={TEXT_PRIMARY}
                  fontSize={22}
                  fontWeight={700}
                >
                  {tier.title}
                </text>
                <text
                  x={TIER_PADDING_X}
                  y={tier.y + tier.height - 24}
                  fill={TEXT_MUTED}
                  fontSize={13}
                >
                  {`${tier.nodes.length} component${tier.nodes.length > 1 ? "s" : ""}`}
                </text>

                {tier.nodes.map((node) => (
                  <DiagramNodePill
                    key={`${tier.title}-${node.label}`}
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.height}
                    fill="rgba(18, 22, 32, 0.95)"
                    stroke={`rgba(122, 169, 255, ${0.45})`}
                    textColor={TEXT_PRIMARY}
                    label={node.label}
                    icon={NODE_ICONS[node.label]}
                    href={DOC_LINKS_STACK[node.label]}
                    shadowId={shadowId}
                    radius={20}
                    maxCharsPerLine={24}
                  />
                ))}

                {index < tiers.length - 1 && (
                  <path
                    d={`M ${VIEWBOX_WIDTH / 2} ${tier.y + tier.height} L ${VIEWBOX_WIDTH / 2} ${
                      tier.y + tier.height + TIER_GAP - 10
                    }`}
                    stroke="rgba(74, 215, 209, 0.85)"
                    strokeWidth={4}
                    strokeLinecap="round"
                    markerEnd={`url(#${arrowMarkerId})`}
                  />
                )}
              </g>
            );
          })}

          <text
            x={VIEWBOX_WIDTH - 60}
            y={60}
            fill={TEXT_MUTED}
            fontSize={14}
            textAnchor="end"
          >
            Click a component for docs
          </text>
        </svg>
      </Stack>
    </Card>
  );
}
